import { colors } from "../theme";

interface TechnologyProps {
  isDark: boolean;
  isVisible: boolean;
  demoActiveNodes: number[];
}

export function Technology({
  isDark,
  isVisible,
  demoActiveNodes,
}: TechnologyProps) {
  const features = [
    {
      title: "Your data, your rules",
      desc: "Encryption, sovereignty, and segmentation at the core. Your data never mingles with anyone else's.",
    },
    {
      title: "Instant-on when you need it",
      desc: "Request your data and we wake exactly what's needed to retrieve it. Nothing more.",
    },
    {
      title: "Smart caching keeps hot data fast",
      desc: "Frequently accessed files stay in our fast tier. Cold data stays cold until you call for it.",
    },
    {
      title: "Built for resilience",
      desc: "Your data is distributed and protected. Hardware failures don't mean data loss.",
    },
  ];

  return (
    <section
      id="technology"
      className="py-32 px-8 relative transition-colors duration-[2000ms]"
      style={{ backgroundColor: isDark ? colors.base02 : colors.base2 }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div
            className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}
          >
            <span
              className="text-sm uppercase tracking-widest mb-4 block"
              style={{ color: colors.magenta }}
            >
              How it works
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Truly off means truly off
            </h2>
            <p
              className="text-xl mb-8"
              style={{ color: isDark ? colors.base01 : colors.base1 }}
            >
              When we say we turn off the lights, we mean it. Your dormant data
              lives on hardware that's physically powered down—not spinning, not
              idle, not in standby. Off.
            </p>

            <div className="space-y-6">
              {features.map((feature, i) => (
                <div key={i} className="flex gap-4">
                  <div
                    className="w-1 rounded-full transition-colors duration-[2000ms]"
                    style={{
                      backgroundColor: isDark ? colors.cyan : colors.blue,
                    }}
                  />
                  <div>
                    <h3 className="font-medium mb-1">{feature.title}</h3>
                    <p
                      className="text-sm"
                      style={{ color: isDark ? colors.base01 : colors.base1 }}
                    >
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mini grid visualization */}
          <div
            className={`relative transition-all duration-700 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}
          >
            <div
              className="aspect-square rounded-3xl p-8 pb-12 relative overflow-hidden transition-colors duration-[2000ms] flex flex-col"
              style={{
                backgroundColor: isDark ? colors.base03 : colors.base3,
                border: `1px solid ${isDark ? colors.base01 : colors.base1}20`,
              }}
            >
              <div className="grid grid-cols-7 grid-rows-3 gap-3 flex-1">
                {Array.from({ length: 21 }, (_, i) => {
                  const isActive = demoActiveNodes.includes(i);
                  return (
                    <div
                      key={i}
                      className="rounded-lg transition-all duration-[2000ms]"
                      style={{
                        backgroundColor: isActive
                          ? isDark
                            ? colors.cyan
                            : colors.magenta
                          : isDark
                            ? colors.base02
                            : colors.base2,
                        boxShadow: isActive
                          ? `0 0 20px ${isDark ? colors.cyan : colors.magenta}50`
                          : "none",
                      }}
                    />
                  );
                })}
              </div>

              <div
                className="absolute bottom-4 left-4 right-4 flex justify-between text-xs"
                style={{ color: isDark ? colors.base01 : colors.base1 }}
              >
                <span>Your data, distributed</span>
                <span style={{ color: isDark ? colors.cyan : colors.magenta }}>
                  {demoActiveNodes.length} nodes awake
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
